import { StoreApi, UseBoundStore } from "zustand";

import { NodeStore } from "@/components/@types";
import { useNode } from "@/components/organisms/Node/Node.hooks";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/components/ui/utils";

/**
 * A node form's submit. With required fields still empty it reads as not
 * ready, and hovering it says what is missing ("Add a title and a contact");
 * pressing it anyway outlines those fields and puts the cursor in the first,
 * rather than doing nothing.
 */
export function NodeSubmitButton({
  store,
  children,
  onSubmit,
  size,
}: {
  store: UseBoundStore<StoreApi<NodeStore>>;
  children: React.ReactNode;
  onSubmit: () => void | Promise<void>;
  size?: "sm" | "md" | "lg";
}) {
  const { events, isDisabled, isSubmitting, missingRequiredLabels } =
    useNode(store);
  const missing =
    missingRequiredLabels.length > 1
      ? `${missingRequiredLabels.slice(0, -1).join(", ")} and ${missingRequiredLabels[missingRequiredLabels.length - 1]}`
      : (missingRequiredLabels[0] ?? "");

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          size={size === "md" ? "default" : size}
          aria-disabled={isDisabled}
          data-testid="node-submit"
          className={cn(isDisabled && "opacity-[0.5]")}
          onClick={() => {
            if (isDisabled) {
              events.onRevealMissingRequired();
              return;
            }
            void onSubmit();
          }}
          disabled={isSubmitting}
        >
          {isSubmitting && <Spinner />}
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className={cn(!isDisabled && "hidden")}>
        {isDisabled ? `Add ${missing}` : ""}
      </TooltipContent>
    </Tooltip>
  );
}
